/**
 * Instagram DM message rules shared by the inbox composer (app) and the send
 * path (services). Instagram DMs are plain text only: no formatting, no
 * attachments, no subject/cc. EPD-12358.
 */

/**
 * Hard cap on an outgoing IG DM, counted on the wire text, i.e. AFTER newlines
 * are escaped to the two-character `\n` the IGDM send path posts (see
 * `escapeIgdmNewlines`). Same limit the legacy InstagramReplyComposer enforces.
 */
export const IGDM_MAX_MESSAGE_LENGTH = 1000;

/** Escape real line breaks to the literal `\n` the IGDM send path posts. */
export function escapeIgdmNewlines(text: string): string {
  return text.replace(/\r\n|\r|\n/g, "\\n");
}

/** Length of an IG DM as Instagram receives it (newlines escaped). */
export function igdmMessageLength(text: string): number {
  return escapeIgdmNewlines(text).length;
}

/**
 * Why an IG DM send failed, as shown to the brand. The send path (services)
 * throws these messages and the inbox composers (app) show them. EPD-12368:
 * every Instagram 503 used to be reported as the 24-hour window, whatever
 * Meta actually said.
 */
export const IGDM_SEND_FAILURE_MESSAGES = {
  /** Meta subcode 2534022 (or 2018278): the creator hasn't messaged the brand in the last 24 hours. */
  MESSAGING_WINDOW_CLOSED:
    "Messaging unavailable. The 24-hour messaging window has ended and messages can only be sent once the creator re-engages with your account.",
  /** Meta subcode 3668004, or the creator's `is_paid_partnership_messages_enabled` is false. */
  CREATOR_NOT_ELIGIBLE:
    "Messaging unavailable. This creator can't receive partnership messages on Instagram.",
  /** Meta marked the error temporary (e.g. code 2, "Service temporarily unavailable"). */
  INSTAGRAM_UNAVAILABLE:
    "Instagram is temporarily unavailable. Please try again later.",
  /**
   * Meta code 10 (outside the window subcodes) or 200: the brand's Instagram connection is missing a
   * permission, e.g. instagram_creator_marketplace_discovery for the Creator Marketplace lookup. The
   * connection is disconnected so the brand is asked to reconnect (EPD-12396).
   */
  CONNECTION_MISSING_PERMISSION:
    "Messaging unavailable. Your Instagram connection is missing a permission Instagram requires. Reconnect Instagram in Settings to send messages.",
  /** Anything else. Must not claim a reason we don't know. Same text as the composers' fallback. */
  UNKNOWN: "Message failed to send",
} as const;

export type IgdmSendFailureReason = keyof typeof IGDM_SEND_FAILURE_MESSAGES;

/** Matched loosely so older wording and prefixed GraphQL errors still map. */
const MESSAGING_WINDOW_MARKER = "24-hour messaging window";

/**
 * The brand-facing reason in a failed IG DM send's raw error text (e.g. a
 * GraphQL error message), or undefined when it isn't a known send failure.
 */
export function igdmSendFailureMessage(raw: string): string | undefined {
  if (raw.includes(MESSAGING_WINDOW_MARKER)) {
    return IGDM_SEND_FAILURE_MESSAGES.MESSAGING_WINDOW_CLOSED;
  }
  return Object.values(IGDM_SEND_FAILURE_MESSAGES).find((message) =>
    raw.includes(message),
  );
}

const HTML_TAG_RE = /<\/?[a-z][^>]*>/i;

/**
 * Convert an HTML message body to the plain text an IG DM can carry. Line
 * breaks and block ends become newlines, every other tag is dropped, and the
 * common text entities are decoded. `&lt;` / `&gt;` are deliberately left
 * encoded so decoding can never re-form a tag. A body with no tags is
 * returned unchanged.
 */
export function igdmHtmlToPlainText(body: string): string {
  if (!HTML_TAG_RE.test(body)) {
    return body;
  }
  return body
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/\n+$/, "");
}
